import { ForesightManager } from "js.foresight";
import { useEffect, useRef } from "react";
import type { RefObject } from "react";

export type UseForesightOptions = {
  callback?: () => void;
  hitSlop?: number;
  name?: string;
  reactivateAfter?: number;
};

export function useForesight<T extends HTMLElement>({
  callback,
  hitSlop,
  name,
  reactivateAfter,
}: UseForesightOptions): {
  elementRef: RefObject<T | null>;
  onFocus: () => void;
} {
  const elementRef = useRef<T>(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const enabled = undefined !== callback;

  useEffect(() => {
    const element = elementRef.current;

    if (!element || !enabled) {
      return;
    }

    const { unregister } = ForesightManager.instance.register({
      element,
      name,
      hitSlop,
      reactivateAfter,
      callback: () => callbackRef.current?.(),
    });

    return unregister;
  }, [enabled, hitSlop, name, reactivateAfter]);

  return {
    elementRef,
    onFocus: () => callbackRef.current?.(),
  };
}
